import { CircularProgress, Box, Collapse, IconButton } from '@mui/material';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWebSocket } from '../../services/useWebSocket'; // Custom hook to manage WebSocket connection
import ChatMessage from '../ChatMessage/ChatMessage';
import ChartMessage from '../ChartMessage/ChartMessage';
import ThinkingMessage from '../ThinkingMessage/ThinkingMessage';
import InputMessage from "../InputMessage/InputMessage";
import { useLang } from '../../Contexts/LanguageContext';

import { useSearchParams } from 'react-router-dom';
import { createThreadApi, getThreadById, updateThread } from "../../APIs/thread";
import { ChatResponse, QuestionItem, ChartData } from '../../types/chat'; // adjust the path as needed
import { useThread } from '../../Contexts/ThreadContext';
import WelcomeScreen from './WelcomeScreen';
import SourceSelector from '../SourceSelector';


// The data
const questions: QuestionItem[] = [
  // --- EDUCATION (#493174) ---
  {
    question: "What are the main barriers to girls' access to education?",
    questionAr: "ما العوائق الرئيسية أمام وصول الفتيات إلى التعليم؟",
    sector: "education",
    color: "#493174",
    category: "education",
  },
  {
    question: "What is the state of learning outcomes in the Arab region?",
    questionAr: "ما حالة نتائج التعلم في المنطقة العربية؟",
    sector: "education",
    color: "#493174",
    category: "education",
  },
  {
    question: "How does the education crisis affect refugee children?",
    questionAr: "كيف تؤثر أزمة التعليم على الأطفال اللاجئين؟",
    sector: "education",
    color: "#493174",
    category: "education",
  },
  {
    question: "How is education financing changing, and what gaps remain?",
    questionAr: "كيف يتغير تمويل التعليم، وما الفجوات المتبقية؟",
    sector: "education",
    color: "#493174",
    category: "education",
  },
  {
    question: "What is the current state of climate change globally?",
    questionAr: "ما هو الوضع الراهن لتغير المناخ عالميًا؟",
    sector: "education",
    color: "#493174",
    category: "environment",
  },
  {
    question: "What is the current status of biodiversity loss worldwide?",
    questionAr: "ما هو الوضع الحالي لفقدان التنوع البيولوجي عالميًا؟",
    sector: "education",
    color: "#493174",
    category: "environment",
  },
  // --- ECONOMY (#EE634D) ---
  {
    question: "What are the economic impacts of extreme weather events?",
    questionAr: "ما هي الآثار الاقتصادية للأحداث المناخية المتطرفة؟",
    sector: "economy",
    color: "#EE634D",
    category: "environment",
  },
  {
    question: "What are the socioeconomic impacts of rapid urbanisation?",
    questionAr: "ما هي الآثار الاجتماعية والاقتصادية للتحضر السريع؟",
    sector: "economy",
    color: "#EE634D",
    category: "general",
  },
  // --- HEALTH (#FBAA3C) ---
  {
    question: "What are the main health challenges in the Arab region?",
    questionAr: "ما هي أبرز التحديات الصحية في المنطقة العربية؟",
    sector: "health",
    color: "#FBAA3C",
    category: "health",
  },
  {
    question: "Compare mortality rates across different regions.",
    questionAr: "قارن معدلات الوفيات عبر المناطق المختلفة.",
    sector: "health",
    color: "#FBAA3C",
    category: "health",
  },
  {
    question: "How is life expectancy evolving across the Arab region?",
    questionAr: "كيف يتطور متوسط العمر المتوقع في المنطقة العربية؟",
    sector: "development",
    color: "#0EA8DA",
    category: "health",
  },
  {
    question: "What is the impact of health spending on health outcomes?",
    questionAr: "ما أثر الإنفاق الصحي على النتائج الصحية؟",
    sector: "economy",
    color: "#EE634D",
    category: "health",
  },
  {
    question: "What progress has been made on maternal and child health?",
    questionAr: "ما التقدم المُحرز في صحة الأم والطفل؟",
    sector: "education",
    color: "#493174",
    category: "health",
  },
  {
    question: "What are the main risk factors for non-communicable diseases?",
    questionAr: "ما عوامل الخطر الرئيسية للأمراض غير المعدية؟",
    sector: "health",
    color: "#FBAA3C",
    category: "health",
  },
  // --- ENVIRONMENT (#1D9E98) ---
  {
    question: "What are the main drivers of greenhouse gas emissions?",
    questionAr: "ما هي الأسباب الرئيسية لانبعاثات غازات الاحتباس الحراري؟",
    sector: "environment",
    color: "#1D9E98",
    category: "environment",
  },
  {
    question: "What causes deforestation in tropical regions?",
    questionAr: "ما أسباب إزالة الغابات في المناطق الاستوائية؟",
    sector: "environment",
    color: "#1D9E98",
    category: "environment",
  },
  {
    question: "How does water scarcity differ across the Arab region?",
    questionAr: "كيف يختلف شُح المياه عبر المنطقة العربية؟",
    sector: "environment",
    color: "#1D9E98",
    category: "environment",
  },
  {
    question: "What does the latest UNEP report say?",
    questionAr: "ماذا يقول أحدث تقرير لبرنامج الأمم المتحدة للبيئة؟",
    sector: "environment",
    color: "#1D9E98",
    category: "environment",
  },
  // --- ARTIFICIAL INTELLIGENCE (#1F5164) ---
  {
    question: "Can you provide a comprehensive analysis of the renewable energy transition?",
    questionAr: "هل يمكنك تقديم تحليل شامل للتحول نحو الطاقة المتجددة؟",
    sector: "artificial_intelligence",
    color: "#1F5164",
    category: "environment",
  },
  {
    question: "Give me a full analysis of plastic pollution — causes, impacts, and solutions.",
    questionAr: "قدّم لي تحليلًا كاملًا لتلوث البلاستيك — الأسباب والآثار والحلول.",
    sector: "artificial_intelligence",
    color: "#1F5164",
    category: "environment",
  },
  // --- DEVELOPMENT (#0EA8DA) ---
  {
    question: "What are the most effective climate mitigation strategies?",
    questionAr: "ما هي أكثر استراتيجيات التخفيف من آثار تغير المناخ فعالية؟",
    sector: "development",
    color: "#0EA8DA",
    category: "environment",
  },
  {
    question: "What policies are governments using to accelerate the energy transition?",
    questionAr: "ما السياسات التي تتبعها الحكومات لتسريع التحول في قطاع الطاقة؟",
    sector: "development",
    color: "#0EA8DA",
    category: "environment",
  },
  {
    question: "What are the latest SDG progress reports?",
    questionAr: "ما أحدث تقارير التقدم في تحقيق أهداف التنمية المستدامة؟",
    sector: "development",
    color: "#0EA8DA",
    category: "general",
  },
  {
    question: "How are developing countries progressing on SDG targets?",
    questionAr: "كيف تتقدم الدول النامية نحو أهداف التنمية المستدامة؟",
    sector: "development",
    color: "#0EA8DA",
    category: "general",
  },
  {
    question: "How is climate change affecting biodiversity and ecosystems?",
    questionAr: "كيف يؤثر تغير المناخ على التنوع البيولوجي والنظم البيئية؟",
    sector: "development",
    color: "#0EA8DA",
    category: "environment",
  },
  {
    question: "Which regions are most vulnerable to climate impacts?",
    questionAr: "أي المناطق الأكثر عرضة لتأثيرات تغير المناخ؟",
    sector: "development",
    color: "#0EA8DA",
    category: "environment",
  },
];


// Mirror of backend extract_chart_json — used to restore chart bubbles on thread reload.
function extractChartJson(text: string): ChartData | null {
  const cleaned = text.replace(/```(?:json)?\s*|\s*```/g, '').trim();
  const start = cleaned.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  for (let i = start; i < cleaned.length; i++) {
    if (cleaned[i] === '{') depth++;
    else if (cleaned[i] === '}') {
      depth--;
      if (depth === 0) {
        try {
          const obj = JSON.parse(cleaned.slice(start, i + 1));
          if (
            Array.isArray(obj.data) && obj.data.length > 0 &&
            ['bar', 'line', 'pie'].includes(obj.type) &&
            obj.xKey && obj.yKey
          ) {
            return obj as ChartData;
          }
        } catch { /* ignore */ }
        break;
      }
    }
  }
  return null;
}

// Strip backend-injected mode instructions from a stored user message. The
// server prepends "[AGENT MODE: …]" / "[SOURCE MODE: …]" blocks (see
// invoke_our_graph) before the user's text; those must never appear in the
// reloaded conversation — only the user's own words should show.
function stripInjectedPrefix(content: string): string {
  return content.replace(/^\s*\[(?:AGENT|SOURCE) MODE:[\s\S]*?\]\s*\n+/, '');
}

function getRandomQuestions<T>(array: T[], count: number): T[] {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

// Pick one random question per sector
function getOnePerSector(array: QuestionItem[]): QuestionItem[] {
  const bySector: Record<string, QuestionItem[]> = {};
  for (const q of array) {
    if (!bySector[q.sector]) bySector[q.sector] = [];
    bySector[q.sector].push(q);
  }
  return Object.values(bySector).map(group => group[Math.floor(Math.random() * group.length)]);
}


const ChatContainer: React.FC = () => {
  const { t, lang, isRTL } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const thread_id = searchParams.get("thread_id");
  const [loading, setLoading] = useState(false);
  const {
    threadId, setThreadId, setIsNewChat,
    threaddbId, setThreaddbId, onThreadCreated, onThreadRenamed, agentMode,
    sourceTitle, setSourceId, setSourceTitle,
  } = useThread();

  // When a report is focused, prompt about that report specifically.
  const inputPlaceholder = sourceTitle
    ? t.inputPlaceholderFocused.replace('{title}', sourceTitle)
    : t.inputPlaceholder;

  // True when the user clicked "New Chat" (or landed on the page) and no thread
  // exists yet. The WelcomeScreen + input are shown, but no API call has been
  // made — the thread is only created when the user actually sends a message.
  const isDraft = !thread_id && !threadId;

  const [messages, setMessages] = useState<ChatResponse[]>([]);
  const [isThinking, setIsThinking] = useState(false);

  const [input, setInput] = useState(''); // User input state
  // We always want the websocket open (draft mode included) so that the very
  // first user message in a brand-new thread can be sent without a reconnect.
  const wsUrl = process.env.REACT_APP_WEBSOCKET_URL!;
  const { response, isOpen, thinkingSteps, chartMessage, sendMessage, cancelQuery } = useWebSocket(wsUrl);

  const messagesRef = useRef<ChatResponse[]>([]);
  // Keep ref in sync with state so effects can read current messages without deps.
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for scrolling to the latest message
  const lastUserMessageRef = useRef<HTMLDivElement>(null); // Ref to anchor the latest user msg at top
  const spacerRef = useRef<HTMLDivElement>(null); // Bottom spacer — sized exactly to fit one viewport
  // Triggers a one-shot "scroll user message to top" on the next render.
  // Set when the user submits a message; cleared by the scroll effect after firing.
  const pinUserToTopRef = useRef(false);
  const lastChartTimestampRef = useRef<string | null>(null);
  // Set to true when the user manually scrolls — disables auto-scroll until next send.
  const userScrolledRef = useRef(false);
  // Tracks the DB id of a thread we just created in this session so the
  // thread_id effect can skip fetchThread without relying on React state batching.
  const justCreatedThreadRef = useRef<string | null>(null);
  // Set to true when a new thread is created; cleared after the first bot
  // response triggers the auto-rename.
  const shouldAutoRenameRef = useRef(false);
  // Tracks the LangGraph thread ID that initiated the current WebSocket stream.
  // The response effect only applies chunks when this matches the active threadId,
  // preventing a stream from one chat from bleeding into another chat's messages.
  const streamingForThreadIdRef = useRef<string | null>(null);

  const questionsSuggested = useMemo(() => {
    const localize = (q: QuestionItem) => ({
      ...q,
      question: lang === 'ar' && q.questionAr ? q.questionAr : q.question,
    });
    if (agentMode === 'environment') {
      const envPool = questions.filter(q => q.category === 'environment').map(q => ({ ...localize(q), color: '#1D9E98' }));
      return getRandomQuestions(getOnePerSector(envPool), 5);
    }
    if (agentMode === 'health') {
      const healthPool = questions.filter(q => q.category === 'health').map(q => ({ ...localize(q), color: '#FBAA3C' }));
      return getRandomQuestions(getOnePerSector(healthPool), 5);
    }
    if (agentMode === 'education') {
      // All education questions share one sector, so skip the one-per-sector pick.
      const eduPool = questions.filter(q => q.category === 'education').map(localize);
      return getRandomQuestions(eduPool, 5);
    }
    return getRandomQuestions(getOnePerSector(questions.map(localize)), 5);
  }, [agentMode, lang]);
  

  useEffect(() => {
    // Skip if no thread is active at all.
    if (!threadId && !thread_id) return;
    // Only apply stream chunks to the thread that sent the request.
    // If the user switched chats mid-stream, the chunks belong to the old thread
    // and must not appear here. fetchThread will reload them when navigating back.
    if (streamingForThreadIdRef.current !== null && streamingForThreadIdRef.current !== threadId) return;
    if (response && response.content?.trim()) {
      setIsThinking(false);
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage && lastMessage.agent_name === response.agent_name) {
          lastMessage.content = response.content;
          lastMessage.response_type = response.response_type;
          return [...prevMessages];
        } else {
          return [...prevMessages, { agent_name: response.agent_name, content: response.content }];
        }
      });

      // Auto-rename the thread once on the first bot response.
      if (shouldAutoRenameRef.current && threaddbId) {
        shouldAutoRenameRef.current = false;
        const firstUserMsg = messagesRef.current.find(m => m.agent_name === 'User');
        if (firstUserMsg?.content) {
          const title = firstUserMsg.content.trim().split(/\s+/).slice(0, 6).join(' ').slice(0, 60);
          updateThread(threaddbId, title).then(() => {
            onThreadRenamed?.(threaddbId, title);
          }).catch(() => {});
        }
      }
    }
  }, [response, thread_id, threadId, threaddbId, onThreadRenamed]);


  // Append chart messages from the WebSocket as first-class chat bubbles.
  // Use a ref to deduplicate in case StrictMode or re-renders fire the effect twice.
  useEffect(() => {
    if (chartMessage && chartMessage.timestamp !== lastChartTimestampRef.current) {
      lastChartTimestampRef.current = chartMessage.timestamp;
      setMessages(prev => [...prev, {
        agent_name: 'chart',
        content: '',
        chartData: chartMessage.chartData,
      }]);
    }
  }, [chartMessage]);

  // Cancels the in-progress query: stops streaming, clears any partial bot
  // response, and re-enables the input so the user can ask something new.
  const handleCancel = () => {
    cancelQuery();
    setIsThinking(false);
    // Remove everything after the last User message (partial/empty bot bubbles).
    setMessages(prev => {
      const lastUserIdx = [...prev].map(m => m.agent_name).lastIndexOf('User');
      return lastUserIdx >= 0 ? prev.slice(0, lastUserIdx + 1) : prev;
    });
  };

  // Handles sending of messages from the user. In draft mode, a UUID is
  // generated client-side so the WS message can be dispatched immediately
  // while the MongoDB thread document is persisted in the background.
  const handleSubmit = async (raw?: string) => {
    const message = (raw ?? input).trim();
    if (!message) return;

    // After this submit's render, scroll the new user message to the top of
    // the viewport once. Subsequent renders (streaming tokens) follow the
    // bottom so the user can read along without manual scrolling.
    pinUserToTopRef.current = true;
    userScrolledRef.current = false;

    if (!threadId) {
      // Draft mode — generate UUID now so we can send WS without waiting for HTTP.
      const newLgThreadId = crypto.randomUUID();

      setMessages(prev => [...prev, { agent_name: 'User', content: message }]);
      setInput('');
      setIsThinking(true);
      shouldAutoRenameRef.current = true;

      // Set threadId immediately so the response effect can start receiving tokens.
      setThreadId(newLgThreadId);
      streamingForThreadIdRef.current = newLgThreadId;

      // Send WS message right now — MongoDB persistence happens in parallel below.
      sendMessage(message, { threadId: newLgThreadId, isNewChat: false });

      // Persist thread to MongoDB in the background; update URL + sidebar when done.
      createThreadApi(newLgThreadId)
        .then(resp => {
          const newThread = resp?.thread;
          if (!newThread?._id) return;
          newThread.configurable = { ...newThread.configurable, thread_name: "New Chat" };
          const newDbId = newThread._id;
          // Set ref BEFORE URL update so the thread_id useEffect skips fetchThread.
          justCreatedThreadRef.current = newDbId;
          setThreaddbId(newDbId);
          onThreadCreated?.(newThread);
          searchParams.set("thread_id", newDbId);
          setSearchParams(searchParams, { replace: true });
        })
        .catch(e => console.error("Failed to create thread:", e));

      return;
    }

    setMessages(prev => [...prev, { agent_name: 'User', content: message }]);
    setInput('');
    setIsThinking(true);
    streamingForThreadIdRef.current = threadId;
    if (isOpen) {
      sendMessage(message);
    }
  };

  // Detect manual scroll — disable auto-scroll until next send.
  // Use wheel/touchmove instead of scroll so programmatic scrollIntoView
  // calls don't accidentally set the flag.
  useEffect(() => {
    const scrollEl = document.querySelector('.messages') as HTMLElement | null;
    if (!scrollEl) return;
    const onUserScroll = () => { userScrolledRef.current = true; };
    scrollEl.addEventListener('wheel', onUserScroll, { passive: true });
    scrollEl.addEventListener('touchmove', onUserScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('wheel', onUserScroll);
      scrollEl.removeEventListener('touchmove', onUserScroll);
    };
  }, []);

  // Recomputes the bottom spacer height after every render so the layout
  // stays stable while the bot replies: the user message stays at the top of
  // the viewport, and the spacer shrinks as bubbles below it grow. Once the
  // spacer hits 0, the page bottom-tracks normally (like before).
  useEffect(() => {
    const userEl = lastUserMessageRef.current;
    const spacerEl = spacerRef.current;
    const scrollEl = userEl?.closest('.messages') as HTMLElement | null;

    const timer = setTimeout(() => {
      let spacerHeight = 0;
      if (userEl && spacerEl && scrollEl) {
        let sum = userEl.getBoundingClientRect().height;
        let cur = userEl.nextElementSibling as HTMLElement | null;
        while (cur && cur !== spacerEl) {
          sum += cur.getBoundingClientRect().height;
          cur = cur.nextElementSibling as HTMLElement | null;
        }
        spacerHeight = Math.max(0, scrollEl.clientHeight - sum);
        spacerEl.style.height = `${spacerHeight}px`;
      }

      if (pinUserToTopRef.current && userEl) {
        pinUserToTopRef.current = false;
        userScrolledRef.current = false;
        userEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (!userScrolledRef.current) {
        if (spacerHeight > 0 && userEl) {
          userEl.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });

    return () => clearTimeout(timer);
  }, [messages, thread_id]);



  function formatAgentName(agentName: string): string {
    return agentName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  const fetchThread = (_threadId: string) => {
    setLoading(true)
    getThreadById(_threadId)
      .then(data => {
        if(!data.thread || data.thread.length === 0) { 
      setMessages([]);
          setLoading(false)
          return;
        }
        setThreadId(data.thread_id)
        const messages = data.thread[1]["channel_values"]["messages"];

        // Step 1: keep only renderable messages (no tool messages, no empty content,
        // no tool-call-only AI messages).
        const renderable = messages.filter(
          (item: { type: any; content: any; tool_calls?: any[] }) =>
            item.type !== "tool" &&
            item.content !== "" &&
            (!item.tool_calls || item.tool_calls.length === 0)
        );

        // Step 2: mirror the live-streaming behavior in backend/graph.py — within a
        // single user turn (between two human messages), if any named sub-agent
        // produced an AI reply, drop the supervisor's wrap-up AI messages for that
        // turn. The supervisor wrap-up is what causes the duplicated answers on
        // reload. If only the supervisor replied (e.g. off-topic refusals), keep it.
        const AGENT_NAMES = new Set([
          "analyser_agent",
          "visualiser_agent",
          "environment_agent",
          "health_agent",
          "general_info_agent",
        ]);

        const turns: any[][] = [];
        let current: any[] = [];
        for (const m of renderable) {
          if (m.type === "human") {
            if (current.length) turns.push(current);
            current = [m];
          } else {
            current.push(m);
          }
        }
        if (current.length) turns.push(current);

        const deduped: any[] = [];
        for (const turn of turns) {
          const hasAgentReply = turn.some(
            (m) => m.type === "ai" && m.name && AGENT_NAMES.has(m.name)
          );
          for (const m of turn) {
            const isSupervisorWrapUp =
              m.type === "ai" && (!m.name || !AGENT_NAMES.has(m.name));
            if (hasAgentReply && isSupervisorWrapUp) continue;
            deduped.push(m);
          }
        }

        const filtered = deduped.map(
          (item: { name: any; content: any; type: any }) => {
            const agentName = item.name || (item.type === "human" ? "User" : "supervisor");
            // Restore chart bubbles from visualiser_agent messages
            if (agentName === "visualiser_agent" && typeof item.content === "string") {
              const chart = extractChartJson(item.content);
              if (chart) {
                return { agent_name: 'chart', content: '', chartData: chart };
              }
            }
            // Hide the server-injected mode instructions from the user's bubble.
            const content = (agentName === "User" && typeof item.content === "string")
              ? stripInjectedPrefix(item.content)
              : item.content;
            return { agent_name: agentName, content, type: item.type };
          }
        ).filter((item: ChatResponse) => {
          // Drop visualiser_agent messages that had no valid chart (raw JSON would confuse user)
          if (item.agent_name === 'visualiser_agent') return false;
          return true;
        });

        // Safety net: collapse consecutive bot bubbles that are (near-)duplicates.
        // A sub-agent's answer and the supervisor's re-emission of it are stored
        // as two messages — sometimes byte-identical, sometimes lightly reworded.
        // The wrap-up dedup above normally removes the supervisor copy, but this
        // guarantees no duplicate survives.
        //
        // We can't just compare a leading prefix: a reworded intro (e.g. "Here's a
        // flowchart…" vs "Here is a flowchart…") shifts the whole string, so
        // prefixes never match even when the bodies are identical. Instead we
        // measure word-overlap (containment) between the two normalised messages —
        // an identical mermaid block + synopses under a different opening sentence
        // still overlaps ~fully and is caught.
        const norm = (s?: string) => (s || '').replace(/\s+/g, ' ').trim();
        const wordsOf = (s?: string) => norm(s).toLowerCase().split(' ').filter(Boolean);
        const isDuplicate = (a?: string, b?: string): boolean => {
          const na = norm(a), nb = norm(b);
          if (!na || !nb) return false;
          if (na === nb) return true;
          const wa = wordsOf(a), wb = wordsOf(b);
          // Short messages: only an exact normalised match counts, so two distinct
          // brief replies are never falsely merged.
          if (wa.length < 12 || wb.length < 12) return false;
          // Length guard: two copies of the same answer are near-equal in size.
          // A short reply followed by a much longer one (e.g. a report list then a
          // flowchart) can share vocabulary but is not a duplicate.
          if (Math.min(na.length, nb.length) / Math.max(na.length, nb.length) < 0.6) return false;
          const setA = new Set(wa), setB = new Set(wb);
          const [small, large] = setA.size <= setB.size ? [setA, setB] : [setB, setA];
          let inter = 0;
          small.forEach(w => { if (large.has(w)) inter++; });
          // ≥85% of the smaller message's vocabulary appears in the larger one.
          return inter / small.size >= 0.85;
        };
        const collapsed: ChatResponse[] = [];
        for (const m of filtered) {
          const prev = collapsed[collapsed.length - 1];
          if (prev && m.agent_name !== 'User' && m.content && prev.content && isDuplicate(prev.content, m.content)) {
            continue;
          }
          collapsed.push(m);
        }
        setMessages(collapsed)
        setLoading(false)
        // Handle the fetched thread data
      })
      .catch(error => {
        console.error('Error fetching thread:', error);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false)
      })
  }



  useEffect(() => {
    if (!thread_id) {
      // URL cleared (new chat / delete) — reset state so welcome screen shows.
      // Cancel any in-progress stream so stale chunks don't bleed into the next
      // thread and so isThinking doesn't stay stuck on true.
      cancelQuery();
      setIsThinking(false);
      streamingForThreadIdRef.current = null;
      setMessages([]);
      setThreadId(null);
      setThreaddbId(null);
      setLoading(false);
      return;
    }

    if (thread_id === "new") {
      setIsThinking(false);
      setMessages([]);
      setLoading(false);
      return;
    }

    // Fresh thread just created by this session — messages are already in state
    // (user message + incoming bot response). Skip fetch entirely.
    if (justCreatedThreadRef.current === thread_id) {
      justCreatedThreadRef.current = null;
      setLoading(false);
      return;
    }

    // Navigating to an existing thread — the stream (if any) continues on the
    // backend for its own thread. Stop showing its spinner here and let
    // fetchThread load this thread's history. The response effect will ignore
    // incoming chunks because streamingForThreadIdRef won't match threadId.
    setIsThinking(false);
    setIsNewChat(false);
    fetchThread(thread_id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread_id])


  return (

    <div className="chat-container">
      <div className={`messages${isRTL ? ' rtl' : ''}`}>
        {loading ? (
          <div style={{ width: '100%', height: "100%", display: 'flex', alignItems: "center", justifyContent: 'center', padding: '8px 16px' }}>
            <CircularProgress />

          </div>
        ) : isDraft && !isThinking ? (
          // No thread yet — show the welcome splash. The thread will be created
          // lazily when the user sends their first message.
          <WelcomeScreen />
        ) : (
          <>
            {(() => {
              // Index of the last User message — used to attach a ref so we
              // can scroll it to the top of the viewport after submit.
              let lastUserIdx = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].agent_name === 'User') { lastUserIdx = i; break; }
              }
              return messages.map((content, index) => {
                const isLastUser = index === lastUserIdx;
                const isActiveStream = isThinking && index === messages.length - 1 && content.agent_name !== 'User';
                if (content.chartData) {
                  return (
                    <ChartMessage
                      key={index}
                      isOpen={isOpen}
                      chartData={content.chartData}
                    />
                  );
                }
                const bubble = (
                  <ChatMessage
                    isOpen={isOpen}
                    key={index}
                    user={content.agent_name === "supervisor" ? "Enmaeya Agent" : formatAgentName(content.agent_name)}
                    content={content.content}
                    type={(content.agent_name !== 'supervisor' && content.agent_name !== 'User') ? 'tool' : 'supervisor'}
                    timestamp={new Date().toLocaleTimeString()}
                    isStreaming={isActiveStream}
                  />
                );
                return isLastUser ? (
                  <div key={index} ref={lastUserMessageRef}>{bubble}</div>
                ) : bubble;
              });
            })()}
            {isThinking && (
              <ThinkingMessage isOpen={isOpen} steps={thinkingSteps} />
            )}
            {/* Bottom spacer — always present so the user message can stay
                anchored at the top of the viewport. Its height is recomputed
                after every render to absorb the streaming reply: starts at
                ~viewport height and shrinks toward 0 as the bot fills the
                space below the user message. Once 0, the page bottom-tracks. */}
            <div ref={spacerRef} style={{ flexShrink: 0 }} aria-hidden="true" />
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      <InputMessage
        onSendMessage={(message) => { handleSubmit(message); }}
        onCancel={handleCancel}
        suggestions={isDraft && !sourceTitle ? questionsSuggested : []}
        placeholder={inputPlaceholder}
        disabled={isThinking}
        leftSlot={<SourceSelector />}
        topSlot={
          <Box sx={{ perspective: '700px' }}>
            <Collapse in={!!sourceTitle} timeout={320} unmountOnExit>
              <Box
                className="report-flip-card"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  maxWidth: '100%',
                  // Flush on top of the input (no gap) and nudged right so the
                  // doc icon sits above the input's doc icon.
                  marginLeft: '8px',
                  marginBottom: 0,
                  padding: '4px 6px 4px 10px',
                  borderRadius: '12px 12px 12px 0',
                  backgroundColor: 'rgba(0,49,79,0.08)',
                  color: '#00314f',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  boxShadow: '0 2px 6px rgba(0,49,79,0.10)',
                  transformOrigin: 'bottom center',
                }}
              >
                <DescriptionRoundedIcon fontSize="small" sx={{ color: '#00314f' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {`${t.focusedOn}: ${sourceTitle ?? ''}`}
                </span>
                <IconButton
                  size="small"
                  onClick={() => { setSourceId(null); setSourceTitle(null); }}
                  sx={{ color: '#00314f', padding: '2px' }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Collapse>
          </Box>
        }
      />
    </div>
  );
}

export default ChatContainer;
